import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  useMediaQuery,
  alpha,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import {
  AcademicCapIcon,
  StarIcon,
  CurrencyDollarIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import {
  AcademicCapIcon as AcademicCapIconSolid,
  StarIcon as StarIconSolid,
  CurrencyDollarIcon as CurrencyDollarIconSolid,
  MapPinIcon as MapPinIconSolid,
} from '@heroicons/react/24/solid';

// Styled Components
const StyledContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: '0 24px 24px 24px',
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.up('xl')]: {
    padding: theme.spacing(6),
  },
  background: theme.palette.background.default,
}));

const StyledCard = styled(Card)(({ theme, isActive }) => ({
  cursor: 'pointer',
  borderRadius: '12px',
  border: isActive 
    ? `1px solid ${alpha('#ffffff', 0.4)}` 
    : `1px solid ${alpha('#ffffff', 0.2)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 1)} 0%, ${alpha(theme.palette.secondary.light, 1)} 100%)`,
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  boxShadow: isActive
    ? `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}, inset 0 1px 0 ${alpha('#ffffff', 0.2)}`
    : `0 4px 16px ${alpha(theme.palette.primary.main, 0.2)}, inset 0 1px 0 ${alpha('#ffffff', 0.1)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${alpha('#ffffff', 0.1)} 0%, ${alpha('#ffffff', 0.05)} 100%)`,
    pointerEvents: 'none',
    borderRadius: '12px',
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.4)}, inset 0 1px 0 ${alpha('#ffffff', 0.3)}`,
    borderColor: alpha('#ffffff', 0.5),
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.85)} 0%, ${alpha(theme.palette.secondary.main, 0.85)} 100%)`,
  },
}));

// Sort options configuration
const SORT_OPTIONS = {
  doctors: [
    { 
      label: 'Seniority wise', 
      value: '-experience_years', 
      Icon: AcademicCapIcon,
      IconSolid: AcademicCapIconSolid
    },
    { 
      label: 'Rating wise', 
      value: '-star_rating', 
      Icon: StarIcon,
      IconSolid: StarIconSolid
    },
    { 
      label: 'Low cost wise', 
      value: 'consultation_fee', 
      Icon: CurrencyDollarIcon,
      IconSolid: CurrencyDollarIconSolid
    },
    { 
      label: 'Nearest to my location', 
      value: 'distance_km', 
      Icon: MapPinIcon,
      IconSolid: MapPinIconSolid,
      requiresLocation: true
    }
  ],
  hospitals: [
    { 
      label: 'Rating wise', 
      value: '-star_rating', 
      Icon: StarIcon,
      IconSolid: StarIconSolid
    },
    { 
      label: 'Nearest to my location', 
      value: 'distance_km', 
      Icon: MapPinIcon,
      IconSolid: MapPinIconSolid,
      requiresLocation: true
    }
  ]
};

const SortingGrid = ({ onSortSelectWithNavigation, currentSort, userLocation, selectedDateRange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Get available sort options based on type and location
  const getAvailableOptions = (type) => {
    const options = SORT_OPTIONS[type] || [];
    return options.filter(option => {
      if (option.requiresLocation) {
        return userLocation && (userLocation.latitude || userLocation.longitude);
      }
      return true;
    });
  };

  const doctorsOptions = getAvailableOptions('doctors');
  const hospitalsOptions = getAvailableOptions('hospitals');

  const handleSortClick = (sortValue, type) => {
    if (onSortSelectWithNavigation) {
      onSortSelectWithNavigation(sortValue, type);
    }
  };

  const SortCard = ({ option, type }) => {
    const isActive = currentSort === option.value;
    const IconComponent = isActive ? option.IconSolid : option.Icon;
    
    // For hospitals, use 6 columns (2 cards per row), for doctors use 3 columns (4 cards in 2x2)
    const gridSize = type === 'doctors' ? 3 : 6;
    
    return (
      <Grid item xs={6} sm={6} md={gridSize}>
        <StyledCard 
          isActive={isActive}
          onClick={() => handleSortClick(option.value, type)}
        >
          <CardContent 
            sx={{ 
              p: { xs: 1, md: 1.5 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              minHeight: { xs: '80px', md: '85px' },
              position: 'relative',
              zIndex: 1,
              '&:last-child': {
                paddingBottom: { xs: 1, md: 1.5 }
              }
            }}
          >
            <Box
              sx={{
                width: { xs: 40, md: 44 },
                height: { xs: 40, md: 44 },
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${alpha('#ffffff', 0.2)} 0%, ${alpha('#ffffff', 0.1)} 100%)`,
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: `1px solid ${alpha('#ffffff', 0.2)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease',
                boxShadow: `inset 0 1px 0 ${theme.palette.text.primary}`,
              }}
            >
              <IconComponent 
                style={{ 
                  width: isMobile ? 22 : 24, 
                  height: isMobile ? 22 : 24,
                  color: 'theme.palette.text.primary'
                }} 
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: isActive ? 700 : 600,
                fontSize: { xs: '0.75rem', md: '0.85rem' },
                color: 'theme.palette.text.primary',
                mt: 0.5,
              }}
            >
              {option.label}
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>
    );
  };

  // Don't render if no options available
  if (doctorsOptions.length === 0 && hospitalsOptions.length === 0) {
    return null;
  }

  return (
    <StyledContainer>
      <Box sx={{ px: { xs: 1, md: 3 } }}>
        {/* Doctors Section */}
        {doctorsOptions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1rem', md: '1.25rem' },
                color: theme.palette.text.primary,
                mb: 1,
                px: { xs: 1, md: 0 },
              }}
            >
              Doctors
            </Typography>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {doctorsOptions.map((option, index) => (
                <SortCard
                  key={option.value || index}
                  option={option}
                  type="doctors"
                />
              ))}
            </Grid>
          </Box>
        )}

        {/* Hospitals Section */}
        {hospitalsOptions.length > 0 && (
          <Box>
            {doctorsOptions.length > 0 && (
              <Divider sx={{ my: 1, opacity: 0.3 }} />
            )}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1rem', md: '1.25rem' },
                color: theme.palette.text.primary,
                mb: 2,
                px: { xs: 1, md: 0 },
              }}
            >
              Hospitals
            </Typography>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {hospitalsOptions.map((option, index) => (
                <SortCard
                  key={option.value || index}
                  option={option}
                  type="hospitals"
                />
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    </StyledContainer>
  );
};

export default SortingGrid;

